import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { 
  GraduationCap, User, BookOpen, Send, CheckCircle,
  Globe, Award, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const africanCountries = [
  "Afrique du Sud", "Algérie", "Angola", "Bénin", "Botswana", "Burkina Faso",
  "Burundi", "Cameroun", "Cap-Vert", "Centrafrique", "Comores", "Congo",
  "Côte d'Ivoire", "Djibouti", "Égypte", "Érythrée", "Eswatini", "Éthiopie",
  "Gabon", "Gambie", "Ghana", "Guinée", "Guinée-Bissau", "Guinée équatoriale",
  "Kenya", "Lesotho", "Liberia", "Libye", "Madagascar", "Malawi", "Mali",
  "Maroc", "Maurice", "Mauritanie", "Mozambique", "Namibie", "Niger",
  "Nigeria", "Ouganda", "RDC", "Rwanda", "São Tomé-et-Príncipe", "Sénégal",
  "Seychelles", "Sierra Leone", "Somalie", "Soudan", "Soudan du Sud",
  "Tanzanie", "Tchad", "Togo", "Tunisie", "Zambie", "Zimbabwe"
];

const russianUniversities = [
  "Université d'État de Moscou (MGU)",
  "Université RUDN",
  "Université de Saint-Pétersbourg",
  "Université Polytechnique de Tomsk",
  "Université de Kazan",
  "Université de Novossibirsk",
  "Institut de Physique et Technologie de Moscou (MIPT)",
  "Université Technique de Bauman",
  "Université de l'Amitié des Peuples (RUDN)",
  "Autre"
];

const fieldsOfStudy = [
  "Médecine", "Ingénierie", "Informatique", "Économie", "Droit",
  "Relations Internationales", "Sciences Politiques", "Linguistique",
  "Mathématiques", "Physique", "Chimie", "Biologie", "Agriculture",
  "Architecture", "Arts et Culture", "Journalisme", "Autre"
];

const StudentApplicationPage = () => {
  const { t, language } = useLanguage();
  const [step, setStep] = useState('form'); // form, success
  const [isLoading, setIsLoading] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    country: '',
    city: '',
    currentEducationLevel: '',
    fieldOfStudy: '',
    targetDegree: '',
    preferredUniversities: [],
    russianLanguageLevel: '',
    motivation: '',
    acceptTerms: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleUniversityChange = (university) => {
    const current = formData.preferredUniversities;
    if (current.includes(university)) {
      setFormData({
        ...formData,
        preferredUniversities: current.filter(u => u !== university)
      });
    } else if (current.length < 3) {
      setFormData({
        ...formData,
        preferredUniversities: [...current, university]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.preferredUniversities.length === 0) {
      toast.error('Veuillez sélectionner au moins une université');
      return;
    }
    
    if (!formData.acceptTerms) {
      toast.error('Vous devez accepter les termes et conditions pour continuer.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/applications/student`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        date_of_birth: formData.dateOfBirth,
        country: formData.country,
        city: formData.city,
        current_education_level: formData.currentEducationLevel,
        field_of_study: formData.fieldOfStudy,
        target_degree: formData.targetDegree,
        preferred_universities: formData.preferredUniversities,
        russian_language_level: formData.russianLanguageLevel,
        motivation: formData.motivation,
        language,
      });

      setApplicationNumber(response.data.application_number);
      setStep('success');
      toast.success(t('studentApplication.form.success'));
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 bg-oar-blue-dark overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-oar-green/10 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <div className="w-20 h-20 bg-oar-green/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <GraduationCap className="w-10 h-10 text-oar-green-light" />
          </div>
          <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-oar-orange font-bold">
            Programme d'Excellence
          </span>
          <h1
            data-testid="student-application-title"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mt-4 mb-6"
          >
            {t('studentApplication.title')}
          </h1>
          <p className="max-w-2xl mx-auto text-lg text-gray-300">
            {t('studentApplication.subtitle')}
          </p>
        </div>
      </section>

      {step === 'form' && (
        <>
          {/* Stats */}
          <section className="py-12 bg-oar-bg-secondary border-b border-oar-border">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-3xl font-heading font-bold text-oar-orange">10 000</div>
                  <div className="text-sm text-oar-text-muted">Étudiants d'ici 2035</div>
                </div>
                <div>
                  <div className="text-3xl font-heading font-bold text-oar-orange">50+</div>
                  <div className="text-sm text-oar-text-muted">Universités partenaires</div>
                </div>
                <div>
                  <div className="text-3xl font-heading font-bold text-oar-orange">100%</div>
                  <div className="text-sm text-oar-text-muted">Bourses couvertes</div>
                </div>
                <div>
                  <div className="text-3xl font-heading font-bold text-oar-orange">25+</div>
                  <div className="text-sm text-oar-text-muted">Pays africains</div>
                </div>
              </div>
            </div>
          </section>

          {/* Application Form */}
          <section className="py-24 bg-oar-bg-primary">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <form onSubmit={handleSubmit} className="space-y-12">
                {/* Personal Information */}
                <div className="bg-white border border-oar-border rounded-lg p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <User className="w-6 h-6 text-oar-blue" />
                    <h2 className="font-heading text-xl text-oar-text-primary">
                      {t('studentApplication.form.personalInfo')}
                    </h2>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-oar-text-secondary text-sm mb-2">
                        {t('studentApplication.form.firstName')} *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        data-testid="app-firstname"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                      />
                    </div>
                    <div>
                      <label className="block text-oar-text-secondary text-sm mb-2">
                        {t('studentApplication.form.lastName')} *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        data-testid="app-lastname"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                      />
                    </div>
                    <div>
                      <label className="block text-oar-text-secondary text-sm mb-2">
                        {t('studentApplication.form.email')} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="app-email"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                      />
                    </div>
                    <div>
                      <label className="block text-oar-text-secondary text-sm mb-2">
                        {t('studentApplication.form.phone')} *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="app-phone"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                      />
                    </div>
                    <div>
                      <label className="block text-oar-text-secondary text-sm mb-2">
                        {t('studentApplication.form.dateOfBirth')} *
                      </label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        required
                        data-testid="app-dob"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                      />
                    </div>
                    <div>
                      <label className="block text-oar-text-secondary text-sm mb-2">
                        {t('studentApplication.form.country')} *
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        data-testid="app-country"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                      >
                        <option value="">Sélectionnez un pays</option>
                        {africanCountries.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-oar-text-secondary text-sm mb-2">
                        {t('studentApplication.form.city')} *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        data-testid="app-city"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                      />
                    </div>
                  </div>
                </div>

                {/* Academic Information */}
                <div className="bg-white border border-oar-border rounded-lg p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <BookOpen className="w-6 h-6 text-oar-blue" />
                    <h2 className="font-heading text-xl text-oar-text-primary">
                      {t('studentApplication.form.academicInfo')}
                    </h2>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-oar-text-secondary text-sm mb-2">
                        {t('studentApplication.form.currentLevel')} *
                      </label>
                      <select
                        name="currentEducationLevel"
                        value={formData.currentEducationLevel}
                        onChange={handleChange}
                        required
                        data-testid="app-level"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="lycee">{t('studentApplication.form.levels.lycee')}</option>
                        <option value="licence">{t('studentApplication.form.levels.licence')}</option>
                        <option value="master">{t('studentApplication.form.levels.master')}</option>
                        <option value="doctorat">{t('studentApplication.form.levels.doctorat')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-oar-text-secondary text-sm mb-2">
                        {t('studentApplication.form.targetDegree')} *
                      </label>
                      <select
                        name="targetDegree"
                        value={formData.targetDegree}
                        onChange={handleChange}
                        required
                        data-testid="app-target"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="licence">{t('studentApplication.form.levels.licence')}</option>
                        <option value="master">{t('studentApplication.form.levels.master')}</option>
                        <option value="doctorat">{t('studentApplication.form.levels.doctorat')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-oar-text-secondary text-sm mb-2">
                        {t('studentApplication.form.fieldOfStudy')} *
                      </label>
                      <select
                        name="fieldOfStudy"
                        value={formData.fieldOfStudy}
                        onChange={handleChange}
                        required
                        data-testid="app-field"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                      >
                        <option value="">Sélectionnez</option>
                        {fieldsOfStudy.map((field) => (
                          <option key={field} value={field}>{field}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-oar-text-secondary text-sm mb-2">
                        {t('studentApplication.form.russianLevel')} *
                      </label>
                      <select
                        name="russianLanguageLevel"
                        value={formData.russianLanguageLevel}
                        onChange={handleChange}
                        required
                        data-testid="app-russian"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="debutant">{t('studentApplication.form.russianLevels.debutant')}</option>
                        <option value="intermediaire">{t('studentApplication.form.russianLevels.intermediaire')}</option>
                        <option value="avance">{t('studentApplication.form.russianLevels.avance')}</option>
                      </select>
                    </div>
                  </div>

                  {/* Universities Selection */}
                  <div className="mb-6">
                    <label className="block text-oar-text-secondary text-sm mb-4">
                      {t('studentApplication.form.preferredUniversities')} * (max 3)
                    </label>
                    <div className="grid md:grid-cols-2 gap-3">
                      {russianUniversities.map((uni) => (
                        <button
                          key={uni}
                          type="button"
                          onClick={() => handleUniversityChange(uni)}
                          className={`text-left p-3 border rounded-sm transition-all ${
                            formData.preferredUniversities.includes(uni)
                              ? 'border-oar-gold bg-oar-gold/10 text-oar-orange'
                              : 'border-oar-border text-oar-text-secondary hover:border-oar-gold/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {formData.preferredUniversities.includes(uni) && (
                              <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            )}
                            <span className="text-sm">{uni}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Motivation */}
                <div className="bg-white border border-oar-border rounded-lg p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <Award className="w-6 h-6 text-oar-blue" />
                    <h2 className="font-heading text-xl text-oar-text-primary">
                      {t('studentApplication.form.motivation')}
                    </h2>
                  </div>

                  <textarea
                    name="motivation"
                    value={formData.motivation}
                    onChange={handleChange}
                    required
                    rows={8}
                    placeholder="Expliquez votre motivation pour étudier en Russie, vos objectifs de carrière et comment ce programme peut vous aider à les atteindre..."
                    data-testid="app-motivation"
                    className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20 resize-none"
                  />
                </div>

                {/* Terms and Conditions Checkbox */}
                <div className="mb-8">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                      data-testid="accept-terms-checkbox"
                      className="mt-1 w-5 h-5 rounded border-oar-border text-oar-blue focus:ring-oar-blue cursor-pointer"
                      required
                    />
                    <span className="text-sm text-oar-text-secondary leading-relaxed">
                      J'ai lu et j'accepte les{' '}
                      <a 
                        href="/terms" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-oar-blue hover:underline font-medium"
                      >
                        Termes et Conditions d'Utilisation
                      </a>{' '}
                      ainsi que la{' '}
                      <a 
                        href="/privacy" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-oar-blue hover:underline font-medium"
                      >
                        Politique de Confidentialité
                      </a>{' '}
                      de l'OAR. Je consens au traitement de mes données personnelles et à leur éventuel transfert vers des universités partenaires en Russie. *
                    </span>
                  </label>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="app-submit"
                  className="w-full flex items-center justify-center gap-2 bg-oar-green hover:bg-oar-green/80 disabled:opacity-50 text-white rounded-sm px-8 py-4 font-medium transition-all duration-300"
                >
                  {isLoading ? (
                    <span>Traitement en cours...</span>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {t('studentApplication.form.submit')}
                    </>
                  )}
                </button>
              </form>
            </div>
          </section>
        </>
      )}

      {/* Success Step */}
      {step === 'success' && (
        <section className="py-24 bg-oar-blue-dark">
          <div className="max-w-xl mx-auto px-6 md:px-12">
            <div className="bg-oar-bg-secondary border border-oar-green/30 rounded-sm p-8 text-center">
              <div className="w-20 h-20 bg-oar-green/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-oar-green" />
              </div>
              <h2 className="font-heading text-3xl text-oar-text-primary mb-4">
                {t('studentApplication.form.success')}
              </h2>
              <p className="text-oar-text-secondary mb-6">
                {t('studentApplication.form.applicationNumber')}
              </p>
              <div className="bg-oar-blue-dark border border-oar-gold p-4 rounded-sm mb-6">
                <span className="text-oar-orange font-heading text-2xl font-bold">
                  {applicationNumber}
                </span>
              </div>
              <p className="text-oar-text-muted text-sm mb-4">
                Un email de confirmation a été envoyé à votre adresse.
              </p>
              <p className="text-oar-text-muted text-sm">
                Notre équipe examinera votre candidature et vous contactera dans les plus brefs délais.
              </p>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default StudentApplicationPage;
